/**
 * LocationService -- Shared location state for all location-dependent widgets.
 *
 * Manages geolocation, city reverse-geocoding, and travel detection.
 */

import type { LocationState } from '../modules/home/types';

const LS_KEY = 'aweni_location';
const TRAVEL_THRESHOLD_KM = 50;

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export class LocationService {
  private state: LocationState = {
    lat: null,
    lng: null,
    city: null,
    lastUpdated: null,
    traveling: false,
  };

  private listeners: Array<() => void> = [];

  constructor() {
    this.loadFromStorage();
  }

  getState(): LocationState {
    return { ...this.state };
  }

  onChange(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  private notify(): void {
    this.listeners.forEach((fn) => fn());
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as LocationState;
        this.state = parsed;
      }
    } catch { /* ignore */ }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(this.state));
    } catch { /* ignore */ }
  }

  async refresh(): Promise<void> {
    if (!('geolocation' in navigator)) return;

    return new Promise<void>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const oldLat = this.state.lat;
          const oldLng = this.state.lng;
          const newLat = pos.coords.latitude;
          const newLng = pos.coords.longitude;

          // Detect travel
          if (oldLat !== null && oldLng !== null) {
            const dist = haversineKm(oldLat, oldLng, newLat, newLng);
            this.state.traveling = dist > TRAVEL_THRESHOLD_KM;
          }

          this.state.lat = newLat;
          this.state.lng = newLng;
          this.state.lastUpdated = Date.now();

          // Reverse geocode city
          await this.reverseGeocode(newLat, newLng);

          this.saveToStorage();
          this.notify();
          resolve();
        },
        () => {
          // Permission denied or error -- keep existing location
          resolve();
        },
        { timeout: 10000, maximumAge: 300000 },
      );
    });
  }

  private async reverseGeocode(lat: number, lng: number): Promise<void> {
    try {
      const resp = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m&timezone=auto`,
        { cache: 'no-store' },
      );
      if (resp.ok) {
        const data = await resp.json();
        // Open-Meteo doesn't return city directly; use timezone as fallback
        const tz = data.timezone as string | undefined;
        if (tz) {
          const parts = tz.split('/');
          this.state.city = parts[parts.length - 1].replace(/_/g, ' ');
        }
      }
    } catch {
      // Use Nominatim as fallback
      try {
        const resp = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
          { headers: { 'User-Agent': 'AWENI-App' } },
        );
        if (resp.ok) {
          const data = await resp.json();
          this.state.city =
            data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.state ||
            null;
        }
      } catch { /* silent */ }
    }
  }

  setManualLocation(lat: number, lng: number, city: string): void {
    this.state.lat = lat;
    this.state.lng = lng;
    this.state.city = city;
    this.state.lastUpdated = Date.now();
    this.state.traveling = false;
    this.saveToStorage();
    this.notify();
  }
}
